import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';

/**
 * Gate for the account console. While the session rehydrates we hold rather
 * than bounce — a redirect fired during rehydrate would sign the owner out of
 * their own console on every reload.
 */
export default function RequireAccount({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAccountAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/account/sign-in"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <>{children}</>;
}

/**
 * Owner-only guard for the billing sections. The API enforces this with
 * `requireEmployeeRole(OWNER)`; this only keeps an admin from walking into a
 * guaranteed 403.
 */
export function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { isOwner, context } = useAccountAuth();
  if (isOwner) return <>{children}</>;

  return (
    <div className="rounded-xl border border-border bg-card px-6 py-12 text-center">
      <h1 className="text-lg font-semibold">Billing is owner-only</h1>
      <p className="mx-auto mt-2 max-w-[48ch] text-sm text-muted-foreground">
        Your role in {context?.organization?.name ?? 'this organization'} is{' '}
        {context?.role?.name ?? 'member'}. Ask the organization owner to change the plan or
        payment details.
      </p>
    </div>
  );
}
